/**
 * <google-wallet-badge> — Google's own "Add to Google Wallet" button, one per
 * save link (order-access.state.ts's walletGoogleMint). A save link carries
 * at most six passes, so the order-level mode renders exactly one badge and
 * only within that ceiling: walletBulkOffered is what holds it there, and
 * above it this mode renders nothing at all rather than the ceil(n/6) row of
 * identical marks a large order would otherwise grow (order-access.state.ts's
 * WALLET_BULK_PASS_CEILING carries the whole reckoning, Apple's half
 * included). Google's brand guidelines say "use only the buttons provided by Google,"
 * never alter font/color/radius/padding, and hold a floor of 48dp that must
 * also be >= every other button on the page. This deployment keeps every one
 * of those terms except the radius, which it overrides deliberately — see the
 * corner note below.
 *
 * So the asset mounts verbatim with NO plate and NO padding of its own — a
 * wrapping plate (e.g. `bg-white p-3` for a light ground in dark scheme)
 * would itself be the guideline breach it was trying to serve, since added
 * padding is exactly what "never alter padding" forbids. The artwork already
 * bakes in its own near-black ground and its own internal padding, and its
 * white wordmark plus the colour Wallet glyph carry it against a dark
 * surface without help — a plate would only add a second frame around an
 * already-framed asset.
 *
 * `h-12` is the 48dp floor, not a taste call: it clears the 40px `size="sm"`
 * icon-buttons it shares the actions row with — satisfying ">= every other
 * button" in the same stroke. Anything smaller (an `h-8` was tried) is a
 * brand-compliance breach, not merely a lighter look.
 *
 * The corner is this app's own `rounded-xl` rung — `--radius-xl`, 0.875rem —
 * the same one `schmancy-button` carries, so the mark sits on the rung every
 * other control in the row does rather than as the one pill among them. That
 * rung is an override of Google's own artwork, made knowingly against the
 * guideline above at the operator's instruction, and it is the ONLY term
 * overridden: font, colour, padding and the 48dp floor stand as published.
 *
 * The override lives in the artwork, not in CSS, because CSS cannot reach it:
 * the corner is drawn by the asset's own `<rect rx>`, and an `overflow-hidden`
 * clip at a smaller radius removes nothing — a pill is wholly inside the
 * rounded rectangle that would clip it. So `web/public/wallet/
 * add-to-google-wallet-{en,de}.svg` carry `rx="16.04"` on both their ground
 * and outline rects: 14px of rendered corner divided by the 48/55 scale
 * `h-12` puts a 55-tall viewBox under. Change `h-12` and that number is stale.
 *
 * The anchor repeats the same rung so the focus ring traces the shape the
 * artwork now draws — nothing clips the mark (no `overflow-hidden`, no ground
 * of its own), so the anchor's corner shapes that ring alone.
 *
 * The email's badge is Google's PNG (ticket-email.wallet-badge.ts, a raster
 * this override does not reach) and stays a pill there.
 *
 * It is deliberately NOT a <schmancy-assist-chip> — that shell supplies the
 * background, radius and padding the guidelines bar.
 *
 * Two terms decide what renders: the slot (order-access.state.ts's
 * walletGoogleSlot — do this order's passes exist at Google) and the mint
 * (walletGoogleMint — did the client's own save-link signing land). `never`
 * renders nothing and the host's `display: contents` means that claims none
 * of its parent's flex gap; a slot still `pending`, or a `ready` slot whose
 * mint is still travelling, holds the mark's exact box with a skeleton in
 * it, so the badge ARRIVES in a footprint the row already had rather than
 * shoving the row's other controls sideways when it lands; a landed mint
 * carries the mark. Dimming the asset itself is what the guidelines forbid,
 * and a skeleton over a reserved box alters nothing about the artwork — it
 * stands where the artwork will be, rather than being a lightened copy of
 * it.
 *
 * A mint that failed reserves NOTHING. A reserved box is a promise that
 * something is arriving into it, and after the mint's bounded retries are
 * spent nothing is: the buyer would be left watching a shimmer that never
 * resolves, which reads worse than the row simply not carrying a Google
 * badge. Its absence costs them nothing they can act on — their on-page
 * ticket, the PNG, the .ics and Apple's own pass are all untouched.
 *
 * Each save link's anchor is a direct child of whatever row mounts the badge,
 * with no wrapper of the badge's own — a wrapper is a single flex item that a
 * narrow row squeezes to one badge's width, folding the rest onto new lines,
 * so the row silently grows tall instead of overflowing and the scroller it
 * sits in never has anything to scroll. `shrink-0` then holds each anchor at
 * the artwork's own width, which is what "never alter padding" and the 48dp
 * floor together amount to under layout pressure: the row overflows and
 * scrolls rather than compressing the mark.
 *
 * This view is zero I/O, same as every other role under packages/views/src —
 * it renders walletGoogleMint, mutates nothing, and never calls
 * mintWalletSaveLinks itself. The mounted app's own order-access.sync.ts is
 * what mints: the moment its listener observes artifacts.walletGoogle read
 * `ready`, it calls the callable and writes the outcome here — before
 * the buyer's finger arrives, never on a click, since a click handler that
 * awaits a round trip before the anchor navigates is defeated by pop-up
 * blockers (order-access.entity.ts's own WalletGoogleSlotSchema doc). The
 * buyer is never shown an error on either path — the wallet pass is an
 * extra, and a failure they cannot act on is noise on the one page they
 * came here to read.
 *
 * The artwork is Google's own, committed under web/public/wallet/
 * from the artwork bundles its brand guidelines publish, in the two locales
 * this app renders. `assetBroken` survives as the honest fallback: should
 * the file ever go missing, every badge hides rather than showing a
 * broken-image glyph or a fabricated look-alike, which the guidelines
 * forbid outright.
 *
 * Local emulator/seed data carries no wallet artifacts at all, so the slot
 * reads `never` there and nothing would render — so in local dev
 * (`import.meta.env.DEV`) one placeholder href stands in on that branch alone,
 * purely so the badge's own layout is on screen to check. It resolves to
 * nothing, is never present outside dev, and never shadows a real `pending`
 * slot, whose reserved box is itself a thing to look at.
 *
 * `ticketId` (paired with `orderAccessToken`), when set, switches the badge
 * into per-ticket mode: ticket-wallet-actions.view.ts's overlay mounts it
 * this way. Per-ticket mode mints nothing on the client — its href points
 * straight at redirectWalletSaveLink's own `/w/g/{token}/{ticketId}` route,
 * which mints a single-object save link on the GET, so this stays zero I/O
 * even though the href it renders is no longer pre-signed. It reads the
 * same slot the bundled mode does, since what a slot says about this
 * order's passes holds regardless of which scope addresses them.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-skeleton>.
import "@mhmo91/schmancy/skeleton";
import { css, html, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { track$ } from "../../analytics/analytics.state.ts";
import { LocalizeMixin, uiLanguage } from "../../i18n/localize.ts";
import { workspaceOrigin } from "../../workspace/workspace.state.ts";
import {
	walletBulkOffered,
	walletGoogleMint,
	walletGoogleSlot,
} from "./order-access.state.ts";

/** Google's own supplied artwork, viewBox 199x55 (over the mandated 48dp
 *  floor), taken from the `add-to-wallet-svg.zip` its brand guidelines
 *  publish and committed under `web/public/wallet/` — tracked, not
 *  the gitignored `public/brand/`, because this asset is Google's and
 *  identical in every fork, unlike a deployment's own wordmark.
 *
 *  Vector, so `h-12` resolves at the device's own pixel ratio: a 55px-tall
 *  raster asked for 144 device pixels on a 3x phone is a ~2.6x upscale, and
 *  the soft mark that produces sits beside Apple's crisp vector mark in the
 *  same row. The same artwork's PNG cut stays committed beside these for
 *  packages/ticket-template's `cid:` embed, where Gmail strips SVG outright.
 *
 *  Same-origin by construction: never a remote URL, which the CSP and the
 *  email's own no-remote-image rule both bar.
 *
 *  Localized per Google's guidelines — "use the localized button where one
 *  is available for your market" — and never re-lettered by us, which the
 *  same guidelines forbid. `en` takes the en-US cut, the only one Google
 *  publishes in English.
 *
 *  Each entry carries the artwork's own intrinsic size, so `h-12 w-auto`
 *  resolves the box from the aspect ratio before the file itself arrives: an
 *  `<img>` with no dimensions is zero-wide until it loads, which is a shift
 *  inside the very state that exists to prevent one. */
const BADGE: Readonly<
	Record<
		"en" | "de",
		{ readonly src: string; readonly width: number; readonly height: number }
	>
> = {
	en: { src: "/wallet/add-to-google-wallet-en.svg", width: 199, height: 55 },
	de: { src: "/wallet/add-to-google-wallet-de.svg", width: 199, height: 55 },
};

@schmancyElement("google-wallet-badge")
export class GoogleWalletBadgeView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: contents;
			}
		`,
	];

	/** Scopes the badge to one ticket, switching it into per-ticket mode —
	 *  paired with `orderAccessToken`, both required together to build the
	 *  redirect href. Null (the default) renders the bundled order-level
	 *  save links instead. */
	@property({ attribute: false }) ticketId: string | null = null;

	/** The token `ticketId`'s per-ticket href resolves against — unused in
	 *  the default bundled mode. */
	@property({ attribute: false }) orderAccessToken: string | null = null;

	/** Flips true the first time the shared badge asset 404s. Every instance
	 *  on the page points at the same file, so one failure means the operator
	 *  hasn't dropped it in yet and every badge hides alike rather than
	 *  showing a broken-image glyph. */
	@state() private assetBroken = false;

	override render(): TemplateResult {
		// Both modes gate on the slot, which is `never` off any status short of
		// paid+complete. The bundled mode needs that gate as much as the
		// per-ticket one despite already holding urls: those urls live in a
		// plain state the mint writes once and only a page unmount clears, so a
		// refund landing on an open page would otherwise leave this badge
		// offering a working save link over cards the same render has just
		// dimmed and captioned "Cancelled". Apple's badge reads a computed and
		// vanishes on that transition by construction; this is what makes the
		// two agree.
		const slot = walletGoogleSlot.get();
		// Per-ticket mode with no token has no href to build from, which is a
		// `never` decidable right here — reserving for it would hold a box open
		// over a control that cannot be constructed.
		const scopedWithoutToken =
			this.ticketId != null && this.orderAccessToken == null;
		// The order-level mode's own ceiling, answered before anything is
		// reserved: above it this mode is not a smaller offer, it is no offer,
		// and a skeleton held here would be a promise of a mark that is never
		// coming. Per-ticket mode is untouched — one ticket is one object, which
		// no ceiling reaches.
		const aboveCeiling = this.ticketId == null && !walletBulkOffered.get();
		if (scopedWithoutToken || aboveCeiling || this.assetBroken) return html``;
		const mint = walletGoogleMint.value;
		// Per-ticket mode carries no client mint at all: its href points straight
		// at redirectWalletSaveLink, which signs a single-object link on the GET.
		// So the bundled mint's own outcome never speaks for it — a bundled mint
		// that failed says nothing about a link the server has yet to sign.
		const realUrls =
			slot !== "ready"
				? []
				: this.ticketId != null
					? [
							`${workspaceOrigin.get()}/w/g/${this.orderAccessToken}/${this.ticketId}`,
						]
					: mint.status === "ready"
						? mint.urls
						: [];
		// The badge's own footprint, held only while the answer is still open:
		// the slot itself `pending`, or `ready` with the bundled mint still
		// travelling (a bounded retry included, which is why a retry never
		// flickers the badge — the state moves off `minting` once, at the
		// outcome). A failed mint is a closed answer and reserves nothing.
		// The reserved box is the artwork's own, measured by mounting the same
		// file the ready state does and taking its intrinsic size, so the two
		// paints agree to the pixel in both locales with nothing to keep in
		// sync. Its `@error` is the same verdict too, so a deployment missing
		// the file reaches the same conclusion during the wait as after it.
		if (
			realUrls.length === 0 &&
			(slot === "pending" || (slot === "ready" && mint.status !== "failed"))
		)
			return html`
				<span
					role="status"
					aria-busy="true"
					aria-disabled="true"
					aria-label=${this.t("storefront.orderAccess.walletGoogleBadge.preparingAriaLabel")}
					class="relative inline-flex shrink-0 rounded-xl"
				>
					<img
						src=${BADGE[uiLanguage()].src}
						width=${BADGE[uiLanguage()].width}
						height=${BADGE[uiLanguage()].height}
						alt=""
						class="h-12 w-auto opacity-0"
						@error=${() => (this.assetBroken = true)}
					/>
					<schmancy-skeleton class="absolute inset-0 rounded-xl"></schmancy-skeleton>
				</span>
			`;
		// Dev-only layout stand-in — see the file doc's own note above. Scoped to
		// the `never` slot, the one case it exists for: local emulator/seed data
		// carries no wallet artifacts at all. A real `pending` slot renders the
		// reserved box above on its own, and a failed mint must read as failed on
		// the dev server too, or the one branch a developer needs to look at is
		// the one the stand-in hides. import.meta.env.VITEST excludes the unit-
		// test run itself, whose whole subject is what each real slot renders.
		const urls =
			slot === "never" && import.meta.env.DEV && !import.meta.env.VITEST
				? (["#"] as const)
				: realUrls;
		if (urls.length === 0) return html``;
		return html`
			${repeat(
				urls,
				(url) => url,
				(url, i) => html`
					<a
						href=${url}
						target="_blank"
						rel="noopener"
						@click=${() =>
							track$.next({ name: "order_action", action: "wallet_google" })}
						aria-label=${
							urls.length > 1
								? this.t(
										"storefront.orderAccess.walletGoogleBadge.ariaLabelIndexed",
										{ index: i + 1, count: urls.length },
									)
								: this.t("storefront.orderAccess.walletGoogleBadge.ariaLabel")
						}
						class="inline-flex shrink-0 rounded-xl"
					>
						<img
							src=${BADGE[uiLanguage()].src}
							width=${BADGE[uiLanguage()].width}
							height=${BADGE[uiLanguage()].height}
							alt=""
							class="h-12 w-auto"
							@error=${() => (this.assetBroken = true)}
						/>
					</a>
				`,
			)}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"google-wallet-badge": GoogleWalletBadgeView;
	}
}
